import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, InputNumber, Select, Card, Row, Col, message } from 'antd'
import useSWR from 'swr'

import Gaps from '../../components/Gaps'
import WeightBasedShippingRuleService from '../../network/services/weightBasedShippingRule'
import TrackingCompanyService from '../../network/services/trackingCompany'
import { serialize } from '../../network/request'
import settingStore from '../../lib/store/app_setting'

const WeightBasedShippingRuleForm = ({
  form = null,
  initialValue = null,
  isLoading = null,
  setIsLoading = null,
  refresh,
  updateAllowed = false,
  createAllowed = false,
  readAllowed = false
}) => {
  const navigate = useNavigate()

  const [isReadOnly, setIsReadOnly] = useState(false)

  // Fetch tracking companies for dropdown
  const { data: trackingCompaniesResponse } = useSWR(serialize(TrackingCompanyService.getAll))

  useEffect(() => {
    //check the access level
    if ((initialValue != null && !readAllowed) || (initialValue === null && !createAllowed)) {
      navigate('/setting_dashboard')
    }
  })

  useEffect(() => {
    setIsReadOnly(!updateAllowed && initialValue != null)
  }, [updateAllowed, initialValue])

  const handleSubmit = async (values) => {
    if (isLoading) return
    setIsLoading(true)

    const redirectToEntry = () => {
      // Open Settings Dashboard on the Weight Based Shipping Rules tab
      settingStore.state.tab = '4'
      navigate('/dashboard/setting_dashboard')
    }

    try {
      if (initialValue?.id) {
        await WeightBasedShippingRuleService.update(initialValue.id, values)
        message.success('Update successful')
      } else {
        await WeightBasedShippingRuleService.create(values)
        message.success('Create successful')
      }
      redirectToEntry()
    } catch (error) {
      const errMsg = error?.message ?? 'Please try again later'
      if (initialValue?.id) {
        message.error('Update weight based shipping rule error ' + errMsg)
      } else {
        message.error('Create weight based shipping rule error ' + errMsg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const trackingCompanies = trackingCompaniesResponse?.data || []

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          minWeight: initialValue?.min_weight,
          maxWeight: initialValue?.max_weight,
          trackingCompanyId: initialValue?.tracking_company_id
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
            <Card bordered={false}>
              <Form.Item
                name="minWeight"
                label="Min Weight (g)"
                rules={[
                  { required: true, message: 'Please enter min weight' },
                  { type: 'number', min: 0, message: 'Weight must be 0 or greater' },
                  () => ({
                    validator(_, value) {
                      if (value == null) return Promise.resolve()
                      if (Number.isInteger(Number(value))) return Promise.resolve()
                      return Promise.reject(new Error('Please enter an integer value'))
                    }
                  }),
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const max = getFieldValue('maxWeight')
                      if (max == null || value == null) return Promise.resolve()
                      if (Number(value) < Number(max)) return Promise.resolve()
                      return Promise.reject(new Error('Min weight must be less than Max weight'))
                    }
                  })
                ]}
              >
                <InputNumber
                  readOnly={isReadOnly}
                  style={{ width: '100%' }}
                  placeholder="Enter min weight in grams"
                  step={1}
                  precision={0}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                name="maxWeight"
                label="Max Weight (g)"
                rules={[
                  { required: true, message: 'Please enter max weight' },
                  { type: 'number', min: 1, message: 'Weight must be greater than 0' },
                  () => ({
                    validator(_, value) {
                      if (value == null) return Promise.resolve()
                      if (Number.isInteger(Number(value))) return Promise.resolve()
                      return Promise.reject(new Error('Please enter an integer value'))
                    }
                  }),
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const min = getFieldValue('minWeight')
                      if (min == null || value == null) {
                        return Promise.resolve()
                      }
                      if (Number(value) > Number(min)) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('Max weight must be greater than Min weight'))
                    }
                  })
                ]}
              >
                <InputNumber
                  readOnly={isReadOnly}
                  style={{ width: '100%' }}
                  placeholder="Enter max weight in grams"
                  step={1}
                  precision={0}
                />
              </Form.Item>

              <Form.Item
                name="trackingCompanyId"
                label="Tracking Company"
                rules={[{ required: true, message: 'Please select a tracking company' }]}
              >
                <Select
                  disabled={isReadOnly}
                  placeholder="Select tracking company"
                  showSearch
                  optionFilterProp="children"
                >
                  {trackingCompanies.map((company) => (
                    <Select.Option key={company.id} value={company.id}>
                      {company.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Gaps />
      </Form>
    </>
  )
}

export default WeightBasedShippingRuleForm
